import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { Business, Person, CalendarToday, Tag } from "@mui/icons-material";
import useApiNew from "../../hooks/useApi";
import { apifilterResultsSummary, method } from "../../apiPath";
import { useSelector } from "react-redux";
import keyNames from "../../keyName";

const TEAL = "#0D9A8F";
const TEAL_DARK = "#0A7A72";
const TEAL_LIGHT = "#e6f7f6";

const ShareStatsPage = () => {
  const { filterId } = useParams();
  const hitApi = useApiNew();

  const filterResultsSummary = useSelector(
    (state) => state.mainReducer.filterResultsSummary
  );
  const isLoading = useSelector((state) => state.mainReducer.isLoading);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (filterId) {
      hitApi(
        `${apifilterResultsSummary}/${filterId}`,
        null,
        method.get,
        keyNames.filterResultsSummary
      );
    }
  }, [filterId, hitApi]);

  if (isLoading === keyNames.filterResultsSummary) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f8fafc"
      >
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  const storeData = filterResultsSummary?.data;
  const filterDataSummary =
    storeData?.campaignFilterId?.filteredData?.summary || {};
  const expiresAt = storeData?.expiresAt;
  const revisionNo = storeData?.revisionNo;

  const crossTab = filterDataSummary?.crossTabulation || {};
  const industries = crossTab?.industries || [];
  const seniorities = crossTab?.seniorities || [];
  const crossTabData = crossTab?.data || {};
  const industryTotals = crossTab?.industryTotals || {};
  const seniorityTotals = crossTab?.seniorityTotals || {};

  const stickyCell = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    bgcolor: "#fff",
    borderRight: "2px solid",
    borderColor: "divider",
    minWidth: 200,
    maxWidth: 200,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const headerStickyCell = {
    ...stickyCell,
    bgcolor: TEAL_DARK,
    color: "#fff",
    zIndex: 3,
    borderRight: "2px solid rgba(255,255,255,0.3)",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: TEAL,
          px: { xs: 2, md: 5 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          color="#fff"
          letterSpacing={0.5}
        >
          Kestone — Campaign Audience Summary
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {revisionNo != null && (
            <Chip
              icon={<Tag sx={{ fontSize: 13, color: "#fff !important" }} />}
              label={`Revision ${revisionNo}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, fontSize: 11 }}
            />
          )}
          {expiresAt && (
            <Chip
              icon={<CalendarToday sx={{ fontSize: 13, color: "#fff !important" }} />}
              label={`Expires ${new Date(expiresAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`}
              size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600, fontSize: 11 }}
            />
          )}
        </Box>
      </Box>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
        {/* Title */}
        <Typography variant="h5" fontWeight={700} color="text.primary" mb={0.5}>
          Expected Audience Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          Summary of contacts and companies targeted for this campaign.
        </Typography>

        {/* ── Stat cards ──────────────────────────────────────────────── */}
        <Grid container spacing={3} mb={5}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 2,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 4 },
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: TEAL_LIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Business sx={{ fontSize: 26, color: TEAL }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} lineHeight={1.1} color="text.primary">
                    {(filterDataSummary.uniqueCompanies || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.3}>
                    Unique Companies
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 2,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 4 },
              }}
            >
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: "#ede9fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Person sx={{ fontSize: 26, color: "#7c3aed" }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} lineHeight={1.1} color="text.primary">
                    {(filterDataSummary.totalContacts || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.3}>
                    Total Contacts
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Cross-tab table ─────────────────────────────────────────── */}
        {industries.length > 0 && (
          <Box>
            <Typography variant="h6" fontWeight={600} mb={1.5} color="text.primary">
              Industry × Seniority Distribution
            </Typography>

            <Paper
              elevation={0}
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                width: "100%",
              }}
            >
              <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                <Table size="small" sx={{ tableLayout: "auto", minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      {/* Sticky industry header */}
                      <TableCell sx={headerStickyCell}>
                        Industry
                      </TableCell>

                      {seniorities.map((sen) => (
                        <TableCell
                          key={sen}
                          align="center"
                          sx={{
                            fontWeight: 700,
                            bgcolor: TEAL,
                            color: "#fff",
                            fontSize: 11,
                            whiteSpace: "nowrap",
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          {sen || "-"}
                        </TableCell>
                      ))}

                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 700,
                          bgcolor: TEAL_DARK,
                          color: "#fff",
                          fontSize: 11,
                          whiteSpace: "nowrap",
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {industries.map((ind, idx) => (
                      <TableRow
                        key={ind}
                        sx={{
                          bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa",
                          "&:hover": { bgcolor: TEAL_LIGHT },
                        }}
                      >
                        {/* Sticky industry name cell */}
                        <TableCell
                          sx={{
                            ...stickyCell,
                            bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa",
                            fontWeight: 500,
                            fontSize: 12,
                            py: 0.75,
                          }}
                          title={ind || "-"}
                        >
                          {ind || "-"}
                        </TableCell>

                        {seniorities.map((sen) => (
                          <TableCell
                            key={sen}
                            align="center"
                            sx={{ fontSize: 12, px: 1.5, py: 0.75, whiteSpace: "nowrap" }}
                          >
                            {(crossTabData[ind]?.[sen] || 0).toLocaleString()}
                          </TableCell>
                        ))}

                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: TEAL_LIGHT,
                            color: TEAL_DARK,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(industryTotals[ind] || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Grand total row */}
                    <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                      <TableCell
                        sx={{
                          ...stickyCell,
                          bgcolor: "#f1f5f9",
                          fontWeight: 700,
                          fontSize: 12,
                          py: 1,
                        }}
                      >
                        Grand Total
                      </TableCell>

                      {seniorities.map((sen) => (
                        <TableCell
                          key={sen}
                          align="center"
                          sx={{ fontWeight: 700, fontSize: 12, px: 1.5, py: 1, whiteSpace: "nowrap" }}
                        >
                          {(seniorityTotals[sen] || 0).toLocaleString()}
                        </TableCell>
                      ))}

                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 800,
                          fontSize: 13,
                          px: 1.5,
                          py: 1,
                          bgcolor: TEAL,
                          color: "#fff",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(filterDataSummary.totalContacts || 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {/* Footer */}
        <Box mt={5} textAlign="center">
          <Typography variant="caption" color="text.disabled">
            Generated on {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · Kestone Integrated Marketing Services
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ShareStatsPage;
